import { useEffect, useState } from 'react'
import { useFirebolt } from '@iq-firebolt/client'
import imageCompression from 'browser-image-compression'

import { imageCompressionOptions } from 'constants'
import { replaceSpecialChars } from 'utils/replaceSpecialChars'
import { buttonTexts, initialLoadingState } from './constants'
import { track } from 'services/redline'

export const useTakePhotoButton = ({
  fileReference,
  side,
  filesReferencesState,
  modifyPayloadKeys,
  handleStepAfterUpload,
  hasUploadError,
  onChange,
  readyForNextStep = false,
  goToNextStep
}) => {
  const { uploadFile, currentStep, clearRemoteFieldError, addFieldRemoteError } = useFirebolt({ fileReference })
  const [filesReferences, setFilesReferences] = filesReferencesState

  const currentFileReference = typeof fileReference === 'string'
  ? fileReference
  : Object.keys(filesReferences).find(key => key.includes(side))

  const [ shouldOpenCamera, setShouldOpenCamera ] = useState(false)
  const [ isModalVisible, setIsModalVisible ] = useState(false)
  const [ hasCameraAllowed, setHasCameraAllowed ] = useState(null)
  const [ isActivePermissionModal, setIsActivePermissionModal ] = useState(null)

  const [ isLoadingUpload, setIsLoadingUpload ] = useState(initialLoadingState)
  const [ buttonText, setButtonText ] = useState(buttonTexts.initial[side])

  const [ isDocumentModalOpen, setIsDocumentModalOpen ] = useState(false)

  const hasUploadedSide = !!filesReferences[currentFileReference]

  const shouldShowDocumentModal = currentStep?.data?.slug === 'identification_data'

  useEffect(() => {
    if(readyForNextStep) {
       goToNextStep() 
    }
  }, [readyForNextStep])

  useEffect(() => {
    if (hasUploadedSide && !hasUploadError) setButtonText(buttonTexts.finished[side])
  }, [filesReferences])

  function openCamera() {
    setShouldOpenCamera(true)
  }

  function openDocumentModal() {
    setIsDocumentModalOpen(true)
  }

  function closeDocumentModal() {
    setIsDocumentModalOpen(false)
  }

  function handleDocumentModalButton() {
    openCamera()
    closeDocumentModal()
  }

  async function checkCameraPermission () {
    const cameraPermission = await navigator.permissions.query({ name: 'camera' })
    const cameraPermissionState = cameraPermission.state !== 'denied'

    setHasCameraAllowed(cameraPermissionState)

    if (!cameraPermissionState) {
      const userAgent = navigator?.userAgent

      track('mediaStatusTracked', {
        userAgent,
        cameraPermission: 'permissionsQueryUndefined'
      })
    }

    return cameraPermissionState
  }

  async function handleButtonCameraOption() {
    const cameraPermissionState = await checkCameraPermission()

    if (cameraPermissionState) {
      if (shouldShowDocumentModal) {
        openDocumentModal()
      } else {
        openCamera()
      }
    } else {
      setIsActivePermissionModal(true)
    }
  }

  async function uploadCameraImage(fileBlob) {
    setShouldOpenCamera(false)
    await handleUpload(side, fileBlob)
  }

  async function handleUpload(side, file) {
    const currentFile = fileReference[side]
      ? fileReference[side]
      : fileReference

    if (file?.name) {
      Object.defineProperty(file, 'name', {
        writable: true,
        value: replaceSpecialChars(file.name),
      })
    }

    const compressedFile = await imageCompression(file, imageCompressionOptions)

    setIsLoadingUpload({ ...isLoadingUpload, [side]: true })
    setButtonText(buttonTexts.loading)

    uploadFileToBackend(compressedFile, currentFile, file)
  }

  function uploadFileToBackend(compressedFile, currentFile, file) {
    uploadFile(compressedFile, currentFile)
      .then(() => {
        clearRemoteFieldError(fileReference)
      })
      .then(() => {
        if(!!modifyPayloadKeys) {
          modifyPayloadKeys({ [fileReference]: true })
          onChange(true)
        }
      })
      .then(() => {
        setFilesReferences({ ...filesReferences, [currentFileReference]: file }) // TODO: esse update no context gera rerender em todos os componentes que consomem o contenxt. investigar uma forma de evitar isso. (task IQL-10276)
      })
      .then(() => {
        if(!!handleStepAfterUpload)
          handleStepAfterUpload()
      })
      .catch(() => {
        setButtonText(buttonTexts.error)
        setTimeout(() => setButtonText(buttonTexts.initial[side]), 5000)
      })
      .finally(() => setIsLoadingUpload({ ...isLoadingUpload, [side]: false }))
  }

  function showConfirmationModal(e) {
    e.stopPropagation()
    setIsModalVisible(true)
  }

  function hideConfirmationModal() {
    setIsModalVisible(false)
  }

  function handleDelete() {
    hideConfirmationModal()
    setButtonText(buttonTexts.initial[side])

    if(!!modifyPayloadKeys) {
      modifyPayloadKeys({ [fileReference]: '' })
    } else {
      addFieldRemoteError(fileReference, 'Empty')
    }

    if (!Array.isArray(fileReference)) {
      return setFilesReferences({
        ...filesReferences,
        [currentFileReference]: '',
      })
    }

    const newRefs = filesReferences?.reduce(
      (acc, nxt) => ({ ...acc, [nxt]: '' }),
      {}
    )

    setFilesReferences({ ...filesReferences, ...newRefs })
  }

  function handleClosePermissionModal () {
    setIsActivePermissionModal(false)
  }

  return {
    openDocumentModal,
    handleDocumentModalButton,
    closeDocumentModal,
    isDocumentModalOpen,
    shouldOpenCamera,
    setShouldOpenCamera,
    openCamera,
    uploadCameraImage,
    isLoadingUpload,
    buttonText,
    showConfirmationModal,
    isModalVisible,
    hideConfirmationModal,
    handleDelete,
    currentFileReference,
    handleButtonCameraOption,
    handleClosePermissionModal,
    hasCameraAllowed,
    isActivePermissionModal,
    hasCameraAllowedState: [hasCameraAllowed, setHasCameraAllowed],
    hasUploadedSide,
    shouldShowDocumentModal
  }
}
